import posthog from "posthog-js";
import { toast } from "sonner";

import { ConnectionErrorToastContent } from "@/components/ConnectionErrorToastContent";
import { ErrorToastContent } from "@/components/ErrorToastContent";
import { isAbortError } from "@/utils/typeGuards/isAbortError";
import { isAxiosError } from "@/utils/typeGuards/isAxiosError";
import { isError } from "@/utils/typeGuards/isError";
import { isNetworkError } from "@/utils/typeGuards/isNetworkError";
import { isOfflineNetworkError } from "@/utils/typeGuards/isOfflineNetworkError";

const ERROR_TOAST_DURATION_MS = 10_000;
// Shared ids: concurrent failures (a multi-file drop) all describe the same condition, so they collapse into one toast
const OFFLINE_TOAST_ID = "offline";
const CONNECTION_TOAST_ID = "connection";

export const logError = (
  error: unknown,
  // showToast: false keeps the console + telemetry trail but says nothing to the user — for a caller that reports its own aggregate outcome (e.g. the unison fan-out, which folds N failed attempts into one message) and must not bury it under one toast per attempt.
  options?: { showToast?: boolean }
) => {
  if (error === null) {
    return;
  }

  // Cancelled requests (input cleared, navigation, tab closed) are expected outcomes — no toast, no exception capture
  if (isAbortError(error)) {
    return;
  }

  console.error(error);

  // toast (sonner) and posthog-js are browser-only. Called from server code (a Server Component, server action, or a server-only data query like getTtvProjects), `toast.error` throws "toast.error is not a function" — which turns a logged, recoverable error into an unhandled 500. On the server, console.error above is the log (Vercel captures it); skip the client-only side effects rather than crash.
  if (typeof window === "undefined") {
    return;
  }

  const shouldShowToast = options?.showToast ?? true;

  // A request that failed because the connection is down is not an app error: explain what happened instead of showing a raw "Network Error", and skip PostHog capture. Only network errors get this treatment — a real bug that happens to occur while offline still surfaces and is captured below.
  if (isOfflineNetworkError(error)) {
    if (shouldShowToast) {
      toast.error(
        "You appear to be offline. Check your internet connection and try again.",
        { id: OFFLINE_TOAST_ID, duration: ERROR_TOAST_DURATION_MS }
      );
    }
    return;
  }

  // A transport failure while online is most often the user's own network (a VPN, a blocking ISP), so the toast leads with what to try instead of a raw "Network Error". Still captured: unlike the offline case it may be our outage.
  if (isNetworkError(error)) {
    if (shouldShowToast) {
      toast.error(ConnectionErrorToastContent(), {
        id: CONNECTION_TOAST_ID,
        duration: ERROR_TOAST_DURATION_MS,
      });
    }
    posthog.captureException(error);
    return;
  }

  if (shouldShowToast && (isAxiosError(error) || isError(error))) {
    toast.error(ErrorToastContent({ error }), {
      duration: ERROR_TOAST_DURATION_MS,
    });
  }
  posthog.captureException(error);
};
